import { GetOrderDetailResponse } from '@/api/type/types';
import { Images } from '@/assets';
import { fonts } from '@/theme/fonts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const MAX_VISIBLE = 10;

interface OrderOptionProps {
  orderDetail: GetOrderDetailResponse;
}
const OrderOption = ({ orderDetail }: OrderOptionProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const { t } = useTranslation();
  const isOverLimit = orderDetail.options.length > MAX_VISIBLE;
  const visibleOptions = expanded
    ? orderDetail.options
    : orderDetail.options.slice(0, MAX_VISIBLE);

  const onClickArrow = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <Container>
      <TitleSection>
        <Img src={Images.OptionIcon} alt="order-detail-option-icon" />
        <Title>{t('orderOptions')}</Title>
      </TitleSection>
      <OptionSection>
        {orderDetail.designOnly && (
          <CheckBoxWrap>
            <CheckBox />
            <CheckBoxTitle>{t('designOnlyDesignServiceOnly')}</CheckBoxTitle>
          </CheckBoxWrap>
        )}
        <OptionWrap>
          {visibleOptions.map((opt) => (
            <Option key={opt}>{opt}</Option>
          ))}
        </OptionWrap>
        {isOverLimit && (
          <DownArrowButton onClick={onClickArrow} data-expanded={expanded}>
            <ArrowImg
              src={Images.OptionDownArrowImg}
              alt="option-down-arrow-img"
              data-expanded={expanded}
            />
          </DownArrowButton>
        )}
      </OptionSection>
      <Box>{orderDetail.comments}</Box>
    </Container>
  );
};

export default OrderOption;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Img = styled.img`
  width: 36px;
  height: 36px;
`;

const Title = styled.div`
  font-size: 14px;
  line-height: 120%;
  color: #769bf7;
`;

const OptionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const OptionWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Option = styled.div`
  height: 32px;
  padding: 0 30px;
  font-size: 14px;
  line-height: 140%;
  color: #fffbfb;
  border-radius: 99px;
  background: #5b4be8;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  &[data-design='true'] {
    background: #df67e2;
  }
`;

const Box = styled.div`
  font-family: ${fonts.family.poppins};
  font-size: 16px;
  line-height: 120%;
  color: #000000;
  margin: 1px;
  padding: 9px 12px;
  height: 225px;
  box-sizing: border-box;
  background: #fff;
  box-shadow: 0px 0px 4px rgba(91, 75, 232, 0.25);
  border-radius: 10px;
  overflow-y: auto;
  text-align: start;
  white-space: pre-wrap;
`;

const CheckBoxWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CheckBox = styled.div`
  width: 20px;
  height: 20px;
  background-image: url(${Images.SelectedDesignCheckBoxImg});
`;

const CheckBoxTitle = styled.div`
  font-size: 15px;
  line-height: 160%;
  color: #212767;
`;

const DownArrowButton = styled.button`
  border: 1px solid;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ArrowImg = styled.img`
  width: 35px;
  height: 14.5px;
  &[data-expanded='true'] {
    transform: rotate(180deg);
  }
`;
